<template>
  <div
    class="custom-logo-slider-container"
    @mouseenter="stopAutoplay"
    @mouseleave="startAutoplay"
  >
    <div class="slider-viewport" ref="viewport">
      <div
        class="slider-track"
        :style="{ transform: `translateX(-${currentOffset}px)`, transition: isTransitioning ? 'transform 0.3s ease-in-out' : 'none' }"
      >
        <div
          v-for="(banner, index) in displayedItems"
          :key="banner.uniqueKey || `${banner.id}-${index}`"
          class="slider-item-wrapper"
        >
          <div class="banner-item">
       
            <SvgIcon v-if="banner.iconName" :name="banner.iconName" class="banner-icon" size="medium" />
           <div class="banner-content">
              <span class="banner-desc">{{ banner.desc }}</span>
              <span class="banner-text">{{ banner.text || `Баннер ${index + 1}` }}</span>
           </div>
          </div>
          
        </div>
      </div>
    </div>
    <button
      v-if="banners.length > 1"
      @click="manualScrollNext"
      class="slider-button next"
      aria-label="Следующий слайд"
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>
  </div>
</template>

<script>
const BANNER_WIDTH = 300; 
const BANNER_MARGIN = 15; 

import SvgIcon from '@/components/ui/SvgIcon'; 

export default {
  name: 'CustomLogoSlider',
  components: {
    SvgIcon,
  },
  props: {
    items: {
      type: Array,
      default: () => [],

    },
    autoplayInterval: {
      type: Number,
      default: 3000, 
    }
  },
  data() {
    return {
      logicalCurrentIndex: 0, 
      viewportWidth: 0, 
      autoplayTimer: null,
      isAutoplayActive: true, 
      isTransitioning: false, 
    };
  },
  computed: {
    banners() {
      return this.items; 
    },
    itemFullWidth() {
      if (this.banners.length === 0) return BANNER_WIDTH + BANNER_MARGIN; 
      return BANNER_WIDTH + BANNER_MARGIN;
    },
    cloneCount() {
      if (this.banners.length === 0) return 0;
      return Math.max(1, this.itemsInViewComputed > 0 ? this.itemsInViewComputed : Math.ceil(this.banners.length / 2));
    },
    displayedItems() {
      if (this.banners.length === 0 || this.banners.length <= this.itemsInViewComputed) {
        return this.banners.map((item, index) => ({ ...item, uniqueKey: `original-${index}` }));
      }
      const headClones = this.banners.slice(-this.cloneCount).map((item, index) => ({ ...item, uniqueKey: `clone-head-${index}` }));
      const tailClones = this.banners.slice(0, this.cloneCount).map((item, index) => ({ ...item, uniqueKey: `clone-tail-${index}` }));
      const originalItems = this.banners.map((item, index) => ({ ...item, uniqueKey: `original-${index}` }));
      return [...headClones, ...originalItems, ...tailClones];
    },
    currentOffset() {
      return (this.logicalCurrentIndex + (this.banners.length > this.itemsInViewComputed ? this.cloneCount : 0)) * this.itemFullWidth;
    },
    itemsInView() {
      if (!this.viewportWidth || this.itemFullWidth === 0) return 1; 
      return Math.floor(this.viewportWidth / this.itemFullWidth);
    },

    itemsInViewComputed() {
      if (!this.banners || this.banners.length === 0) return 0;
      if (!this.viewportWidth || !this.itemFullWidth) return 1; 
      return Math.max(1, Math.floor(this.viewportWidth / this.itemFullWidth));
    },
    canScrollNext() {
      return this.banners.length > 0 && this.banners.length > this.itemsInViewComputed;
    }
  },
  methods: {
    manualScrollNext() {
      this.stopAutoplay(true); 
      this.scrollNext();
    },
    scrollNext() {
      if (this.banners.length === 0 || this.banners.length <= this.itemsInViewComputed) {
        if (this.logicalCurrentIndex !== 0 && this.banners.length > 0) this.logicalCurrentIndex = 0;
        return;
      }

      this.isTransitioning = true;
      this.logicalCurrentIndex++;

      if (this.logicalCurrentIndex >= this.banners.length) {
        setTimeout(() => {
          this.isTransitioning = false; 
          this.logicalCurrentIndex = 0; 
          this.$nextTick(() => {
            setTimeout(() => {
              this.isTransitioning = true;
            }, 50);
          });
        }, 300); 
      }
    },
    updateViewportWidth() {
      if (this.$refs.viewport) {
        this.viewportWidth = this.$refs.viewport.offsetWidth;
      }
    },
    startAutoplay() {
      this.isAutoplayActive = true;
      if (this.autoplayInterval > 0 && !this.autoplayTimer) {
        this.autoplayTimer = setInterval(() => {
          if (this.isAutoplayActive) {
            this.scrollNext();
          }
        }, this.autoplayInterval);
      }
    },
    stopAutoplay(manualStop = false) {
      this.isAutoplayActive = false; 
      if (manualStop) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }
  },
  created() {
  },
  mounted() {
    this.$nextTick(() => {
      this.updateViewportWidth();
      this.startAutoplay(); 
    });
    window.addEventListener('resize', this.updateViewportWidth);
  },
  beforeUnmount() {
    clearInterval(this.autoplayTimer); 
    this.autoplayTimer = null;
    window.removeEventListener('resize', this.updateViewportWidth);
  },
};
</script>

<style scoped lang="scss">
.custom-logo-slider-container {
  position: relative;
  display: flex;
  align-items: center;
}

.slider-viewport {
  width: 100%;
  overflow: hidden;
}

.slider-track {
  display: flex;
  transition: transform 0.3s ease-in-out;
  height: 100%;
}

.slider-item-wrapper {
  flex: 0 0 auto; 
  width: #{300px}; 
  margin-right: 15px; 
  box-sizing: border-box; 

  &:last-child {
    margin-right: 0;
  }
}

.banner-item {
  text-align: left;
  width: 300px;
  height: 100px;
  background-color: white;
  border-radius: 1.5rem; 
  display: flex;
  gap: 20px;
  padding: 0 20px;
  align-items: center;
  font-size: 12px;
  color: #ccc;
  overflow: hidden; 

}

.banner-icon {
  font-size: 32px; 
  color: $primary; 
}

.banner-text {
  font-weight: bolder;
  font-size: 20px;
  color: rgba(0,161,176,.5);
}

.banner-item span {

}
.banner-content {
  display: flex;
  flex-direction: column;
}

.banner-desc {
  
}

.slider-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 30px;
  height: 100%;
  background-color: $bondi-blue;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: $white; 
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  z-index: 10;
  border: none;
  padding: 0;

  &:hover {
    background-color: white;
    color: $bondi-blue;

  }

  svg {
    width: 16px; 
    height: 16px;
  }

  &.next {
    right: 0px; 
  }
}
</style>
